/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";

export type MatchBucket = "live" | "up" | "past";
const STATUS: Record<MatchBucket, string> = { live: "IN_PROGRESS", up: "SCHEDULED", past: "ENDED" };
const PAGE = 50;
const MAX_PAGES = 10;

/** Set once the backend answers 404 for the lightweight endpoint (older deployment) so we stop trying it. */
let summaryUnavailable = false;

const fetchPage = async (endpoint: string, tournamentId: string, bucket: MatchBucket, page: number) => {
  const res = await Api.get(endpoint, { params: { tournamentId, status: STATUS[bucket], page, size: PAGE } });
  return (res?.data?.matchResponseList ?? []) as any[];
};

/**
 * All matches of a tournament for one status (the page filters, groups and counts client-side).
 * Uses the lightweight summary endpoint and falls back to the full one if the backend does not have it yet.
 */
export const fetchAll = async (tournamentId: string, bucket: MatchBucket) => {
  const out: any[] = [];
  let endpoint: string = summaryUnavailable ? QUERY_KEY.matchByTournamentIdAndStatusPageable : QUERY_KEY.matchSummaryByTournamentIdAndStatusPageable;
  for (let page = 0; page < MAX_PAGES; page++) {
    let list: any[];
    try {
      list = await fetchPage(endpoint, tournamentId, bucket, page);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (page === 0 && endpoint === QUERY_KEY.matchSummaryByTournamentIdAndStatusPageable && (status === 404 || status === 405)) {
        summaryUnavailable = true;
        endpoint = QUERY_KEY.matchByTournamentIdAndStatusPageable;
        list = await fetchPage(endpoint, tournamentId, bucket, page);
      } else throw e;
    }
    out.push(...list);
    if (list.length < PAGE) break;
  }
  return out;
};

export const useTournamentMatches = (tournamentId?: string | null) => {
  const useBucket = (bucket: MatchBucket) =>
    useQuery({ queryKey: ["tpMatches", tournamentId, bucket], queryFn: () => fetchAll(tournamentId as string, bucket), enabled: !!tournamentId, staleTime: 30_000, refetchInterval: bucket === "live" ? 30_000 : false });
  const live = useBucket("live"), up = useBucket("up"), past = useBucket("past");
  return {
    live: live.data ?? [], up: up.data ?? [], past: past.data ?? [],
    loading: live.isLoading || up.isLoading || past.isLoading,
    error: live.isError && up.isError && past.isError,
    refetch: () => { void live.refetch(); void up.refetch(); void past.refetch(); },
  };
};
